import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import { suppressKeyboardEventForInput } from 'components/AgGrid/utils';
import { WorkInSumTitles } from 'constantsProject';
import type { WorkOrderPriceData, WorkInSumEnum } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { WorkRatioEditCellRendererComponent } from '../components/WorkRatioEditCellRendererComponent';
import { PassageHeaderComponent } from '../PassageHeaderComponent';

interface IUseTypeWorkColumnsProps {
    workOrderStatus: WorkOrderStatusEnum | undefined;
    workOrderHasInProgress?: boolean;
    onWorkRatioEditSuccess?: () => void;
    hasEmptyWorkRatios?: boolean;
    useAgCellRowDataRefresh?: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams?: any;
}

const cellClass = 'workKind';

export const groupNameHeaderName = 'Виды работ';

export const useTypeWorkColumns = (props: IUseTypeWorkColumnsProps) => {
    const {
        workOrderStatus,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        onWorkRatioEditSuccess,
        hasEmptyWorkRatios,
    } = props;

    return useMemo<(ColDef<WorkOrderPriceData> | ColGroupDef<WorkOrderPriceData>)[]>(
        () => [
            {
                headerName: groupNameHeaderName,
                field: 'groupName',
                headerClass: `flexStart ${cellClass}`,
                children: [
                    {
                        headerName: 'Шифр',
                        field: 'workCode',
                        width: 136,
                        headerClass: cellClass,
                        cellClass,
                    },
                    {
                        headerName: 'Наименование',
                        field: 'workName',
                        width: 170,
                        headerClass: cellClass,
                        cellClass,
                    },
                    {
                        headerName: 'Ед.изм.',
                        field: 'workUnit',
                        width: 90,
                        headerClass: cellClass,
                        cellClass,
                    },
                    {
                        headerName: 'Участие в суммировании ФО по виду работ',
                        field: 'workInSum',
                        width: 235,
                        headerClass: `noWrap ${cellClass}`,
                        valueFormatter: ({ data }) => WorkInSumTitles[data?.workInSum as WorkInSumEnum],
                        cellClass,
                    },
                    {
                        headerName: 'К перехода',
                        headerComponent: PassageHeaderComponent,
                        headerComponentParams: {
                            hasEmptyWorkRatios,
                        },
                        cellRenderer: WorkRatioEditCellRendererComponent,
                        cellRendererParams: {
                            useAgCellRowDataRefresh,
                            useAgCellRowDataRefreshParams,
                            workOrderStatus,
                            onSuccess: onWorkRatioEditSuccess,
                        },
                        field: 'workRatio',
                        width: 136,
                        headerClass: cellClass,
                        cellClass,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.workRatio, 4),
                        suppressKeyboardEvent: suppressKeyboardEventForInput,
                    },
                    {
                        headerName: 'План (Кол-во)',
                        field: 'workPlan',
                        width: 136,
                        headerClass: cellClass,
                        cellClass,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.workPlan, 3),
                    },
                    ...(workOrderStatus === WorkOrderStatusEnum.InProgress
                        ? [
                              {
                                  headerName: 'Выполнено (Кол-во)',
                                  field: 'workCompleted',
                                  width: 136,
                                  headerClass: cellClass,
                                  cellClass,
                                  valueFormatter: ({ data }: any) => formatNumberToRu(data?.workCompleted, 3),
                              },
                              {
                                  headerName: 'Остаток (Кол-во)',
                                  field: 'workReminder',
                                  width: 136,
                                  headerClass: cellClass,
                                  cellClass,
                                  valueFormatter: ({ data }: any) => formatNumberToRu(data?.workReminder, 3),
                              },
                          ]
                        : []),
                ],
            },
        ],
        [
            hasEmptyWorkRatios,
            onWorkRatioEditSuccess,
            useAgCellRowDataRefresh,
            useAgCellRowDataRefreshParams,
            workOrderStatus,
        ],
    );
};
